import { useBetraegeSichtbar, verdeckt } from "../../../app/betraege";
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading } from "../../../components/States";
import { Toolbar } from "../../../components/ui";
import { date, money as moneyFmt } from "../../../lib/format";
import { Ansichtswahl, KoopLeer } from "./bausteine";
import { useHeute, useKooperationen } from "./hooks";
import { FILTERS, baueZeilen, boardSpalten, creatorFilterOptionen, markenFilterOptionen, passtFilter, passtSuche, zaehleFilter, type KoopFilter } from "./model";
import { t, ta } from "../../../lib/sprache";
import { NurSchreiben } from "../../../app/nurLesen";

/**
 * Kooperationen als Board nach aktuellem Status (11_app_agentur/deals-board.html). Dieselbe Menge und dieselben Filter wie die
 * Liste (Creator, Marke, Suche, Gruppe); die Spalten scrollen waagrecht und behalten ihre Breite (Befund 26).
 */
export function Board() {
  const sicht = useBetraegeSichtbar();
  const money = verdeckt(sicht, moneyFmt);
  const ws = useStaffWorkspace();
  const q = useKooperationen();
  const today = useHeute();
  const [params, setParams] = useSearchParams();
  const filter = (FILTERS.some((f) => f.key === params.get("filter")) ? params.get("filter") : "aktiv") as KoopFilter;
  const search = params.get("q") ?? "";
  const creator = params.get("creator") ?? "";
  const marke = params.get("marke") ?? "";
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const zeilen = useMemo(() => (q.data ? baueZeilen(q.data, { today, timeZone: ws.org.timezone, selbst: !!ws.isCreator }) : []), [q.data, today, ws.org.timezone]);

  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending || !q.data) return <Loading />;
  const d = q.data;
  const gesucht = zeilen.filter((z) => (!creator || z.deal.talent_id === creator) && (!marke || z.deal.brand_id === marke) && passtSuche(z, search));
  const counts = zaehleFilter(gesucht);
  const spalten = boardSpalten(gesucht.filter((z) => passtFilter(z, filter)));

  return (
    <main id="main" className="content tk-koop">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{ta("Agentur-Arbeitsbereich", "Agency workspace", "Dein Creator-Konto", "Your creator account")}</div>
          <h1>{ta("Kooperationen", "Deals", "Deals", "Deals")}</h1>
          <p>{ta("Dieselben Kooperationen wie in der Liste, nach aktuellem Status.", "The same deals as in the list, by current status.", "Dieselben Deals wie in der Liste, nach aktuellem Status.", "The same deals as in the list, by current status.")}</p>
        </div>
        <Ansichtswahl aktuell="board">
          {/* Nur-Lesen (Test-Chat 04.11.2026): Anlegen entfällt ausdrücklich, nicht nur über die Regel in portal.css */}
          <NurSchreiben><Link className="btn primary" to="/agentur/kooperationen/neu">{ta("Neue Kooperation", "New deal", "Neuer Deal", "New deal")}</Link></NurSchreiben>
        </Ansichtswahl>
      </div>
      {zeilen.length === 0 ? (
        <KoopLeer talents={q.data?.talents ?? []} />
      ) : (
        <>
          <Toolbar
            search={search}
            onSearch={(v) => setParam("q", v)}
            label={t("Board durchsuchen", "Search board")}
            placeholder={t("Suchen …", "Search …")}
            filters={FILTERS.map((f) => ({ ...f, count: counts[f.key] }))}
            filter={filter}
            onFilter={(f) => setParam("filter", f === "aktiv" ? "" : f)}
          >
            <select hidden={ws.isCreator} aria-label={t("Nach Creator filtern", "Filter by creator")} value={creator} onChange={(e) => setParam("creator", e.target.value)}>
              <option value="">{t("Alle Creator", "All creators")}</option>
              {creatorFilterOptionen(d.talents, zeilen, creator).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
            <select aria-label={t("Nach Marke filtern", "Filter by brand")} value={marke} onChange={(e) => setParam("marke", e.target.value)}>
              <option value="">{t("Alle Marken", "All brands")}</option>
              {markenFilterOptionen(d.brands, zeilen, marke).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </Toolbar>
          {spalten.length === 0 ? (
            <div className="panel stack">
              <p>{t("Keine passenden Ergebnisse. Passe die Suche oder die Filter an.", "No matching results. Adjust the search or the filters.")}</p>
              <div className="row"><button onClick={() => setParams(new URLSearchParams(), { replace: true })}>{t("Filter zurücksetzen", "Reset filters")}</button></div>
            </div>
          ) : (
            <div className="tk-koop-board" role="region" aria-label={t("Board nach Status", "Board by status")} tabIndex={0}>
              {spalten.map((s) => (
                <section key={s.key} className="panel stack tk-koop-spalte" aria-label={`${s.pill.label}, ${s.zeilen.length}`}>
                  <h3>{s.pill.label} · {s.zeilen.length}</h3>
                  {s.zeilen.map((z) => (
                    <Link key={z.deal.id} className="panel stack tk-koop-karte" to={`/agentur/kooperationen/${z.deal.id}`}>
                      <strong>{z.talentName}</strong>
                      <p>{z.brandName} · {z.deal.title}</p>
                      {z.honorarCents != null ? <span className="num">{money(z.honorarCents, z.deal.currency)}</span> : <span className="muted">{z.modell}</span>}
                      <small>{z.frist ? `${date(z.frist.date)} · ${z.frist.art}${z.frist.ueberfaellig ? t(" · überfällig", " · overdue") : ""}` : t("Keine offene Frist", "No open deadline")}</small>
                      {z.kampagne && <small>{t("Kampagne ", "Campaign ")}{z.kampagne.title}</small>}
                      {z.status.hinweis && <small>{z.status.hinweis}</small>}
                    </Link>
                  ))}
                </section>
              ))}
            </div>
          )}
          <p className="source-note">{ta("Spalten nach dem Status der Liste: aus Rechnungen und Anforderungen abgeleitet, sonst Arbeitsstand der Agentur. Das Board scrollt waagrecht.", "Columns follow the list status: derived from invoices and requests, otherwise the agency's working status. The board scrolls horizontally.", "Spalten nach dem Status der Liste: aus Rechnungen abgeleitet, sonst dein Arbeitsstand. Das Board scrollt waagrecht.", "Columns follow the list status: derived from invoices, otherwise your working status. The board scrolls horizontally.")}</p>
        </>
      )}
      {api.mode === "demo" && <p className="demo-note">{t("Demo · Alle Daten sind erfunden.", "Demo · All data is fictional.")}</p>}
    </main>
  );
}
